// Spot per client (myta-streams docs/plans/2026-09-08-spot-per-client.md §2 "interruption"). The lease API tells
// this page, on every renew, whether the GPU box its seat sits on is being reclaimed by AWS (a spot instance) and
// whether on-demand capacity has already been prepared for THIS participant. This module is the pure half: what
// the renew payload means, when to move, and what to tell the user — kept free of React so it is unit-testable.
//
// The contract (myta-streams api/leases.ts `drainingSurface`):
//   renew 200 → { draining: true, drainingKind: 'interrupt'|'rebalance', interruptAt?, deadline?,
//                 replacement: { status: 'ready'|'pending'|'none', nodeId?, phase? } }
//   renew 409 → { error: 'node_gone', cause?: 'spot-interrupt' }  — the box is already gone.

import type {
  ILeaseReplacement,
  IMytaLease,
} from '@services/MytaStreamsService';

export type DrainKind = 'interrupt' | 'rebalance';

export interface IDrainNotice {
  kind: DrainKind;
  /** When this page first learned about it (ms). */
  noticedAt: number;
  /** The interruption notice's own time and EC2's deadline, ms — interruption only. */
  interruptAt?: number;
  deadline?: number;
  replacement?: ILeaseReplacement;
}

/** Move before EC2 pulls the plug even when the replacement is still warming up: a cold re-entry beats a frozen frame. */
export const HANDOFF_LEAD_MS = 25_000;
/** Renew cadence while an interruption notice is live — the normal 45 s is too coarse for a two-minute clock. */
export const DRAINING_RENEW_INTERVAL_MS = 5_000;

const toMs = (iso?: string): number | undefined => {
  const ms = Date.parse(iso ?? '');
  return Number.isFinite(ms) ? ms : undefined;
};

export function noticeFromRenew(
  renewed: Pick<
    IMytaLease,
    'draining' | 'drainingKind' | 'interruptAt' | 'deadline' | 'replacement'
  >,
  nowMs: number,
  previous: IDrainNotice | null = null,
): IDrainNotice | null {
  if (!renewed.draining) {
    return null;
  }
  const kind: DrainKind =
    renewed.drainingKind === 'interrupt' ? 'interrupt' : 'rebalance';
  return {
    kind,
    // Keep the first sighting so "noticed N s ago" does not reset on every renew; a rebalance that turns into an
    // interruption is a new notice.
    noticedAt: previous && previous.kind === kind ? previous.noticedAt : nowMs,
    interruptAt: kind === 'interrupt' ? toMs(renewed.interruptAt) : undefined,
    deadline: kind === 'interrupt' ? toMs(renewed.deadline) : undefined,
    replacement: renewed.replacement,
  };
}

export type HandoffAction = 'none' | 'wait' | 'handoff';

export interface IHandoffDecision {
  action: HandoffAction;
  reason: string;
}

// When to leave a box that is being reclaimed. A rebalance recommendation alone never moves anyone (the seat may
// outlive the risk); an interruption moves the user as soon as somewhere to go exists, or shortly before the
// deadline regardless, or immediately when the pool said it could not prepare anything (the fresh lease request
// IS the replacement then, and the sooner it is asked the sooner capacity comes).
export function decideHandoff(
  notice: IDrainNotice | null,
  nowMs: number,
): IHandoffDecision {
  if (!notice || notice.kind !== 'interrupt') {
    return {
      action: 'none',
      reason: notice ? 'rebalance recommendation — seat stays' : 'not draining',
    };
  }
  const status = notice.replacement?.status;
  if (status === 'ready') {
    return { action: 'handoff', reason: 'on-demand replacement is ready' };
  }
  if (status === 'none') {
    return {
      action: 'handoff',
      reason: 'no replacement could be pre-placed — asking the ladder now',
    };
  }
  if (
    notice.deadline !== undefined &&
    notice.deadline - nowMs <= HANDOFF_LEAD_MS
  ) {
    return {
      action: 'handoff',
      reason: `deadline in ${Math.max(
        0,
        Math.round((notice.deadline - nowMs) / 1000),
      )} s`,
    };
  }
  return {
    action: 'wait',
    reason:
      status === 'pending'
        ? 'replacement is starting'
        : 'replacement not booked yet',
  };
}

export type NoticeSeverity = 'info' | 'warning' | 'error';

export interface ISpotNotice {
  title: string;
  body: string;
  severity: NoticeSeverity;
  /** Seconds until EC2 acts, when known. */
  secondsLeft?: number;
}

const fmtClock = (ms?: number): string =>
  ms === undefined
    ? ''
    : new Date(ms).toLocaleTimeString([], { hour12: false });

export function describeNotice(
  notice: IDrainNotice,
  nowMs: number,
): ISpotNotice {
  if (notice.kind === 'rebalance') {
    return {
      severity: 'warning',
      title: 'Heads-up: this streaming machine may be reclaimed soon',
      body: 'The cloud provider flagged the machine that renders your session as at risk of being taken back. If that happens you will be moved to a new machine automatically and your session will restart — nothing to do right now.',
    };
  }
  const secondsLeft =
    notice.deadline !== undefined
      ? Math.max(0, Math.round((notice.deadline - nowMs) / 1000))
      : undefined;
  const when =
    notice.deadline !== undefined ? ` at ${fmtClock(notice.deadline)}` : '';
  const status = notice.replacement?.status;
  const body =
    status === 'ready'
      ? `A new machine is ready — moving you now. Your session will restart from the loading screen (about 30–60 s).`
      : status === 'none'
      ? `No spare machine was available to prepare in advance, so we are requesting a new one now — expect a longer wait while it starts. You will be moved automatically.`
      : `A new machine is being prepared for you${
          secondsLeft !== undefined
            ? ` — you will be moved within ${secondsLeft} s`
            : ''
        }, before the current one goes away${when}. Your session will restart when that happens.`;
  return {
    severity: 'error',
    title: 'Your streaming machine is being reclaimed by the cloud provider',
    body,
    secondsLeft,
  };
}

export function describeGone(cause?: string): ISpotNotice {
  return cause === 'spot-interrupt'
    ? {
        severity: 'error',
        title: 'The cloud provider reclaimed your streaming machine',
        body: 'Reconnecting you to a new machine now — your session restarts from the loading screen. A replacement was prepared when the notice arrived, so this should take well under a minute.',
      }
    : {
        severity: 'error',
        title: 'Your streaming machine went away',
        body: 'The machine that rendered your session is no longer running. Reconnecting you to a new one now — your session restarts from the loading screen.',
      };
}

// A renew answered 404 lease_not_current: the machine is alive, the SEAT is not ours any more (its hold
// lapsed, an operator freed it, another session took it). `describeGone` would lie here — nothing died — so
// this says only what the user can act on: the place is gone and we are getting them another one.
export function describeSeatLost(cause?: string): ISpotNotice {
  const body =
    'Your session is restarting on a free place — this page reconnects by itself, from the loading screen.';
  return cause === 'superseded'
    ? {
        severity: 'error',
        title: 'Another session took over your place',
        body,
      }
    : cause === 'operator'
    ? {
        severity: 'error',
        title: 'Your place was freed by an operator',
        body,
      }
    : {
        severity: 'error',
        title: 'Your place in the experience was released',
        body,
      };
}

export function describeHandoffInProgress(reason: string): ISpotNotice {
  return {
    severity: 'warning',
    title: 'Moving you to a new machine…',
    body: `${reason}. Your session is restarting on another GPU box — hold on, this page reconnects by itself.`,
  };
}
